import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { featureLayer, dynamicMapLayer } from "esri-leaflet";

import { mapConfig, REGRID_TILE_URL } from "../../config/mapConfig";
import { PARCEL_SOURCES, sourceInView } from "../../config/parcelSources";

/**
 * Parcel boundaries are dense; only show them once the user is zoomed into a
 * neighbourhood. Below this the lines turn into unreadable noise and the
 * county services get hammered with huge requests.
 *
 * listing-map.js (listing detail page) copies this, PARCEL_STYLE (and its
 * satellite variant) and parcelDynamicLayers; keep them in step.
 */
export const MIN_PARCEL_ZOOM = 14;

const PARCEL_COLOR = "#4aba91";
const PARCEL_WEIGHT = 0.75; // CSS px
const PARCEL_OPACITY = 0.65;

// Thin, faint lines get lost on aerial imagery: heavier and near opaque there.
const PARCEL_WEIGHT_SATELLITE = 1.5;
const PARCEL_OPACITY_SATELLITE = 0.95;

const PARCEL_STYLE: L.PathOptions = {
  color: PARCEL_COLOR,
  weight: PARCEL_WEIGHT,
  opacity: PARCEL_OPACITY,
  fill: false,
  interactive: false,
};

const PARCEL_STYLE_SATELLITE: L.PathOptions = {
  ...PARCEL_STYLE,
  weight: PARCEL_WEIGHT_SATELLITE,
  opacity: PARCEL_OPACITY_SATELLITE,
};

/**
 * A parcel style for the MapServer (dynamicMapLayer) counties. Those services
 * draw the image themselves in their own colours (Bonneville #ffaa00,
 * Madison #ffc814), so each export request carries a dynamicLayers renderer
 * override: an unfilled outline in the same colour, width and opacity.
 * - Width: ArcGIS uses points and esri-leaflet exports at dpi 96: px * 72 / 96.
 * - Opacity: in the colour's alpha, not the layer, so an edge shared by two
 *   parcels stacks the way Leaflet's per-path stroke opacity does.
 */
function parcelDynamicLayers(layerIds: number[], style: L.PathOptions) {
  const rgb = parseInt(PARCEL_COLOR.slice(1), 16);
  const outline = {
    type: "esriSLS",
    style: "esriSLSSolid",
    color: [
      (rgb >> 16) & 255,
      (rgb >> 8) & 255,
      rgb & 255,
      Math.round((style.opacity ?? PARCEL_OPACITY) * 255),
    ],
    width: ((style.weight ?? PARCEL_WEIGHT) * 72) / 96,
  };
  return layerIds.map((id) => ({
    id,
    source: { type: "mapLayer", mapLayerId: id },
    drawingInfo: {
      renderer: {
        type: "simple",
        symbol: { type: "esriSFS", style: "esriSFSNull", outline },
      },
    },
  }));
}

/**
 * Toggleable parcel/property-line overlay.
 *
 * - "regrid"      → one raster tile layer, nationwide (needs a paid token).
 * - "freeCounties"→ free public county GIS services (esri-leaflet). Each
 *                    county that has a verified service is attached only when
 *                    the map is zoomed in and actually over that county;
 *                    areas with no free service simply show nothing.
 */
export default function ParcelLayer({
  visible,
  satellite,
}: {
  visible: boolean;
  satellite: boolean;
}) {
  const map = useMap();
  const provider = mapConfig.parcelProvider;

  // --- Regrid raster tiles -------------------------------------------------
  useEffect(() => {
    if (!visible || provider !== "regrid") return;

    const layer = L.tileLayer(`${REGRID_TILE_URL}?token=${mapConfig.regridToken}`, {
      maxNativeZoom: 20,
      maxZoom: 22,
      opacity: 0.85,
      // Keep parcels under the price pins so markers stay clickable.
      pane: "overlayPane",
    });
    layer.addTo(map);
    return () => {
      layer.remove();
    };
  }, [visible, provider, map]);

  // --- Free public county services (esri-leaflet) --------------------------
  const layersRef = useRef<Map<string, L.Layer>>(new Map());

  useEffect(() => {
    if (provider !== "freeCounties") return;
    const layers = layersRef.current;
    const style = satellite ? PARCEL_STYLE_SATELLITE : PARCEL_STYLE;

    const reconcile = () => {
      const show = visible && map.getZoom() >= MIN_PARCEL_ZOOM;
      const b = map.getBounds();
      const view = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      };

      for (const src of PARCEL_SOURCES) {
        const want = show && sourceInView(src, view);
        const existing = layers.get(src.key);

        if (want && !existing) {
          const layer =
            src.kind === "featureLayer"
              ? featureLayer({
                  url: src.url,
                  // esri-leaflet's default where is "1=1"; passing undefined
                  // overwrites it and breaks the query (Teton ID had no where).
                  where: src.where || "1=1",
                  style: () => style,
                })
              : dynamicMapLayer({
                  url: src.url,
                  layers: src.layers,
                  // Draw in PARCEL_STYLE, not the county's own symbology.
                  dynamicLayers: src.layers ? parcelDynamicLayers(src.layers, style) : undefined,
                  // Opacity is already in the outline alpha (parcelDynamicLayers).
                  opacity: 1,
                  pane: "overlayPane",
                });
          layer.addTo(map);
          layers.set(src.key, layer);
        } else if (!want && existing) {
          existing.remove();
          layers.delete(src.key);
        }
      }
    };

    reconcile();
    map.on("moveend", reconcile);

    return () => {
      map.off("moveend", reconcile);
      layers.forEach((l) => l.remove());
      layers.clear();
    };
  }, [visible, satellite, provider, map]);

  return null;
}
